/**
 * FeedbackDialog — three-step feedback flow
 *
 * Step 1: Thumbs up / Thumbs down choice.
 * Step 2: Textarea for elaboration (prompt adapts to sentiment).
 * Step 3: Success state.
 *
 * On "Send", invokes the `send-feedback` Edge Function:
 *   { sentiment, message, userEmail }
 * No feedback data is written to the client-side DB.
 * Errors surface via toast without closing the dialog.
 */

import { useState } from "react";
import { ThumbsUp, ThumbsDown, Loader2, CheckCircle2 } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button }   from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { supabase } from "@/integrations/supabase/client";
import { toast }    from "@/hooks/use-toast";
import { trackSupportFeedbackSent } from "@/lib/analytics";

// ─────────────────────────────────────────────
// TYPES
// ─────────────────────────────────────────────

type Sentiment = "up" | "down" | null;
type Step      = 1 | 2 | 3;

interface FeedbackDialogProps {
  open:    boolean;
  onClose: () => void;
}

// ─────────────────────────────────────────────
// COMPONENT
// ─────────────────────────────────────────────

export function FeedbackDialog({ open, onClose }: FeedbackDialogProps) {
  const [step,      setStep]      = useState<Step>(1);
  const [sentiment, setSentiment] = useState<Sentiment>(null);
  const [text,      setText]      = useState("");
  const [isSending, setIsSending] = useState(false);

  const reset = () => {
    setStep(1);
    setSentiment(null);
    setText("");
    setIsSending(false);
  };

  const handleOpenChange = (isOpen: boolean) => {
    if (!isOpen) {
      onClose();
      // Small delay so the dialog close animation plays before clearing state.
      setTimeout(reset, 300);
    }
  };

  const handleSentimentClick = (choice: "up" | "down") => {
    setSentiment(choice);
    setStep(2);
  };

  const handleSend = async () => {
    if (!sentiment || !text.trim()) return;

    setIsSending(true);
    try {
      const { data: { session }, error: sessionError } = await supabase.auth.getSession();
      if (sessionError || !session?.user) throw new Error("No active session.");

      const { error } = await supabase.functions.invoke("send-feedback", {
        body: {
          sentiment,
          message:   text.trim(),
          userEmail: session.user.email,
        },
      });

      if (error) throw error;

      trackSupportFeedbackSent({
        sentiment,
        messageLength: text.trim().length,
      });
      setStep(3);
    } catch (err) {
      const msg = err instanceof Error ? err.message : "An unexpected error occurred.";
      toast({
        title:       "Could not send feedback",
        description: msg,
        variant:     "destructive",
      });
    } finally {
      setIsSending(false);
    }
  };

  const textareaPrompt =
    sentiment === "up"
      ? "What do you love about Office Rhythm?"
      : "How can we improve?";

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-sm">

        {/* ── Step 1: Sentiment choice ── */}
        {step === 1 && (
          <>
            <DialogHeader className="text-center items-center">
              <DialogTitle className="text-lg">How is your experience?</DialogTitle>
              <DialogDescription className="text-sm text-muted-foreground mt-1">
                We'd love to hear what you think.
              </DialogDescription>
            </DialogHeader>

            {/* Sprint 3 — Bug 15: both chips must render at strictly
                identical dimensions. We give each a fixed `w-32 h-28`
                footprint (padding + icon + label) so the differing
                label lengths ("Loving it" vs "Needs work") can no
                longer stretch one side relative to the other. */}
            <div className="flex gap-4 mt-2 justify-center">
              <button
                onClick={() => handleSentimentClick("up")}
                className="group w-32 h-28 flex flex-col items-center justify-center gap-2 rounded-2xl border border-border/60 bg-card hover:border-green-400 hover:bg-green-50 dark:hover:bg-green-950/30 transition-all tap-highlight no-select"
                aria-label="Thumbs up"
              >
                <ThumbsUp className="h-8 w-8 text-muted-foreground group-hover:text-green-500 transition-colors" />
                <span className="text-xs font-medium text-muted-foreground group-hover:text-green-600 dark:group-hover:text-green-400 transition-colors">
                  Loving it
                </span>
              </button>

              <button
                onClick={() => handleSentimentClick("down")}
                className="group w-32 h-28 flex flex-col items-center justify-center gap-2 rounded-2xl border border-border/60 bg-card hover:border-red-400 hover:bg-red-50 dark:hover:bg-red-950/30 transition-all tap-highlight no-select"
                aria-label="Thumbs down"
              >
                <ThumbsDown className="h-8 w-8 text-muted-foreground group-hover:text-red-500 transition-colors" />
                <span className="text-xs font-medium text-muted-foreground group-hover:text-red-600 dark:group-hover:text-red-400 transition-colors">
                  Needs work
                </span>
              </button>
            </div>
          </>
        )}

        {/* ── Step 2: Text elaboration ── */}
        {step === 2 && (
          <>
            <DialogHeader>
              <div className="flex items-center gap-2 mb-1">
                {sentiment === "up"
                  ? <ThumbsUp   className="h-4 w-4 text-green-500 flex-shrink-0" />
                  : <ThumbsDown className="h-4 w-4 text-red-500 flex-shrink-0" />
                }
                <DialogTitle className="text-base leading-snug">
                  {textareaPrompt}
                </DialogTitle>
              </div>
              <DialogDescription className="text-xs text-muted-foreground">
                Your feedback goes directly to the team. We read every response.
              </DialogDescription>
            </DialogHeader>

            <Textarea
              placeholder="Share your thoughts…"
              value={text}
              onChange={(e) => setText(e.target.value)}
              className="resize-none min-h-[100px] mt-1"
              autoFocus
            />

            <div className="flex gap-2 mt-1">
              <Button
                variant="outline"
                className="flex-1"
                onClick={() => setStep(1)}
                disabled={isSending}
              >
                Back
              </Button>
              <Button
                className="flex-1 gap-2"
                onClick={handleSend}
                disabled={isSending || !text.trim()}
              >
                {isSending && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
                {isSending ? "Sending…" : "Send Feedback"}
              </Button>
            </div>
          </>
        )}

        {/* ── Step 3: Success state ── */}
        {step === 3 && (
          <div className="flex flex-col items-center gap-4 py-4 text-center">
            <div className="sr-only">
              <DialogTitle>Success</DialogTitle>
              <DialogDescription>Your feedback has been sent successfully.</DialogDescription>
            </div>
            <div className="h-14 w-14 rounded-full bg-green-500/10 flex items-center justify-center">
              <CheckCircle2 className="h-7 w-7 text-green-500" />
            </div>
            <div>
              <p className="text-base font-semibold">Thanks for helping us improve!</p>
              <p className="text-sm text-muted-foreground mt-1">
                Your feedback shapes the future of Office Rhythm.
              </p>
            </div>
            <Button onClick={() => handleOpenChange(false)} className="w-full mt-1">
              Done
            </Button>
          </div>
        )}

      </DialogContent>
    </Dialog>
  );
}